import React, { useRef, useEffect, useState } from 'react';

interface AudioWaveformProps {
  audioLevel: number;
  isRecording: boolean;
}

const AudioWaveform: React.FC<AudioWaveformProps> = ({ audioLevel, isRecording }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const [bars, setBars] = useState<number[]>([]);

  useEffect(() => {
    if (!isRecording) {
      setBars([]);
      return;
    }

    // Update bars based on audio level
    const newBars = Array.from({ length: 20 }, (_, i) => {
      const baseLevel = audioLevel * 100; // Scale audio level
      const barHeight = Math.max(2, Math.min(50, baseLevel * (1 + Math.random() * 0.5)));
      return barHeight;
    });

    setBars(newBars);
  }, [audioLevel, isRecording]);

  useEffect(() => {
    if (!canvasRef.current || !isRecording) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw waveform bars
      const barWidth = canvas.width / bars.length;
      const centerY = canvas.height / 2;

      bars.forEach((barHeight, index) => {
        const x = index * barWidth;
        const height = barHeight;
        
        // Create gradient
        const gradient = ctx.createLinearGradient(x, centerY - height, x, centerY + height);
        gradient.addColorStop(0, '#3b82f6'); // Blue
        gradient.addColorStop(0.5, '#1d4ed8'); // Darker blue
        gradient.addColorStop(1, '#3b82f6'); // Blue

        // Draw bar
        ctx.fillStyle = gradient;
        ctx.fillRect(x + 1, centerY - height / 2, barWidth - 2, height);

        // Add glow effect
        ctx.shadowColor = '#3b82f6';
        ctx.shadowBlur = 5;
        ctx.fillRect(x + 1, centerY - height / 2, barWidth - 2, height);
        ctx.shadowBlur = 0;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [bars, isRecording]);

  if (!isRecording) {
    return null;
  }

  return (
    <div className="bg-gray-900 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-white">Live Audio Level</h4>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs text-gray-300">Recording</span>
        </div>
      </div>
      
      <div className="bg-gray-800 rounded p-3">
        <canvas
          ref={canvasRef}
          width={300}
          height={60}
          className="w-full h-15"
        />
      </div>
      
      <div className="mt-2 text-center">
        <span className="text-xs text-gray-400">
          Audio Level: {(audioLevel * 100).toFixed(1)}%
        </span>
      </div>
    </div>
  );
};

export default AudioWaveform; 